import type { ImgHTMLAttributes, ReactElement, SyntheticEvent } from 'react';
import { useState } from 'react';

import { SessionStorage } from '../../storage/session/SessionStorage';
import { useSettingsSlice } from '../../store/slices/settings/selectors';
import { Loader } from '../loader';

import styles from './LazyLocalizedImageConnected.module.css';

export interface LazyLocalizedImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  loaderClassName?: string;
  containerClassName?: string;
}

const DEFAULT_LANG = 'en';
const LOC = '{loc}';

/**
 * A component that renders a localized image lazily.
 * If src includes {loc} it will be replaced with player selected language.
 * In case if the language in question doesn't have localized image, will fall back to default language (english)
 *
 * Utilizes sessionStorage to avoid unnecessary requests in case of unmounting
 *
 * @component
 * @param {LazyLocalizedImageProps} props - The component props, same as `<img/>` but with required src and no onLoad and onError plus loader class and container class name
 * @returns {React.Element} The rendered component.
 */

export const LazyLocalizedImageConnected = ({
  containerClassName,
  src,
  loaderClassName = '',
  alt = '',
  ...rest
}: LazyLocalizedImageProps): ReactElement => {
  const locale = useSettingsSlice.currentLanguage();
  const [isLoading, setLoad] = useState(true);
  const fallback = src.replace(LOC, DEFAULT_LANG) ?? '';

  const source = SessionStorage.get(src) ?? src.replace(LOC, locale);

  const errorHandle = (e: SyntheticEvent<HTMLImageElement, Event>): void => {
    rest?.onError?.(e);
    const target = e.target as HTMLImageElement;
    // Using dataset instead of state to avoid re-rendering the whole component on src switch
    if (target.dataset.fallback !== 'true') {
      target.src = fallback;
      SessionStorage.set(src, fallback);
      target.dataset.fallback = 'true';
    } else {
      setLoad(false);
    }
  };

  const onLoadHandle = (e: SyntheticEvent<HTMLImageElement, Event>): void => {
    rest?.onLoad?.(e);
    setLoad(false);
  };

  return (
    <div className={containerClassName}>
      {isLoading ? (
        <span className={`${styles.loader} ${loaderClassName}`}>
          <Loader />
        </span>
      ) : null}
      <img src={source} alt={alt} onLoad={onLoadHandle} onError={errorHandle} {...rest} />
    </div>
  );
};
